import { Component, OnDestroy, OnInit } from '@angular/core';
import { EstablishmentService } from '../establishment.service';
import { ActivatedRoute } from '@angular/router';
import { DetailsBaseComponent } from '../../../../../core/base/components/details-base/details-base.component';
import { EstablishmentModel } from '../../../../../core/models/establishment.model';
import { CustomerGroupService } from '../../../settings/customer-group/customer-group.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../../core/enums/permission.enum';
import { EstablishmentStatusEnum, establishmentTranslation } from '../../../../../core/enums/establishment-status.enum';
import { GedDocumentTypeEnum } from '../../../../../core/enums/ged-document-type.enum';
import { EstablishmentTypeEnum } from '../../../../../core/enums/establishment-type.enum';

@Component({
  selector: 'vex-establishment-details',
  templateUrl: './establishment-details.component.html',
  styleUrls: ['./establishment-details.component.scss'],
})
export class EstablishmentDetailsComponent
  extends DetailsBaseComponent<EstablishmentModel>
  implements OnInit, OnDestroy
{
  EstablishmentStatusEnum = EstablishmentStatusEnum;
  establishmentStatusTranslation = establishmentTranslation;
  warningMessages = [];

  hasMainBuilding = false;
  hasRib = false;
  hasInsurance = false;
  hasBillingAddress = false;

  init = false;
  private needsTabAdded = false;
  establishmentId: any;
  establishment: EstablishmentModel;
  defaultTabs = [
    {
      label: 'INFORMATIONS',
      route: './',
      routerLinkActiveOptions: { exact: true },
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_INFORMATION,
      index: 1,
    },
    {
      label: 'BATIMENTS',
      route: './buildings',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_BUILDINGS,
      index: 2,
    },
    {
      label: 'ACTIVITÉS',
      route: './activite',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_ACTIVITY,
      index: 3,
    },
    {
      label: 'CONTACT INTERNE',
      route: './contact',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_CONTACT,
      index: 4,
    },
    {
      label: 'ACTEURS ACHATS',
      route: './validators',
      // PRED-3130 : droit dédié, indépendant de la lecture de l'établissement (masquable pour un Déclarant).
      permission: ENUM_PERMISSIONS.ESTABLISHMENT_READ_PURCHASE_ACTORS,
      index: 5,
    },
    {
      label: 'SUPERVISION MÉTIER',
      route: './supervision',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_SUPERVISION,
      index: 6,
    },
    {
      label: 'PLAN ANALYTIQUE',
      route: './analytics',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_ANALYTIC_PLAN,
      index: 7,
    },
    {
      label: 'DOCUMENTS',
      route: './documents',
      permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_GED,
      index: 8,
    },
  ];

  constructor(
    public service: EstablishmentService,
    public activatedRoute: ActivatedRoute,
    private customerGroupService: CustomerGroupService,
    public authService: AuthService,
  ) {
    super(service, activatedRoute, authService);
  }

  ngOnInit() {
    super.ngOnInit();
    this.init = false;

    this.subscription.add(
      this.customerGroupService.getCurrent().subscribe(data => {
        this.addTabs({
          label: 'PLAN ANALYTIQUE',
          route: './analytics',
          additionalCondition: data?.data.comptable?.analyticPlan,
          permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_ANALYTIC_PLAN,
          index: 7,
        });
      }),
    );

    const item = this.service.entity$.subscribe(establishment => {
      this.manageNeedsTab(establishment);

      if (establishment?._id) {
        this.generateWarningText(establishment);

        if (!this.init) {
          this.init = true;
          this.authService.getCurrentUserPermissions$().subscribe(permissions => {
            if (permissions.includes(ENUM_PERMISSIONS.READ_ESTABLISHMENT)) {
              this.service.getContact(establishment._id).subscribe();
            }
          });
        }
      }
    });

    this.subscription.add(item);

    const routeSub = this.activatedRoute.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.establishmentId = id;
        // Reload establishment data when navigating within the same route
        const findSub = this.service.findById(id).subscribe();
        this.subscription.add(findSub);
      }
    });
    this.subscription.add(routeSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  generateWarningText(entity: EstablishmentModel) {
    this.hasMainBuilding = !!entity.mainBuilding?._id;
    this.hasRib =
      entity.requiredDocuments && !!entity.requiredDocuments.find(x => x.documentType === GedDocumentTypeEnum.RIB);
    this.hasInsurance =
      entity.requiredDocuments &&
      !!entity.requiredDocuments.find(x => x.documentType === GedDocumentTypeEnum.INSURANCE_CERTIFICATE);
    this.hasBillingAddress = !!entity.billingAddressId || !!entity.billingEmail;
    this.warningMessages = [];

    if (!this.hasMainBuilding) {
      this.warningMessages.push('Un bâtiment principal doit être sélectionné');
    }

    if (!this.hasRib) {
      this.warningMessages.push('Un RIB doit être renseigné dans les documents');
    }

    if (!this.hasInsurance) {
      this.warningMessages.push("Une attestation d'assurance doit être renseignée dans les documents");
    }

    if (!this.hasBillingAddress) {
      this.warningMessages.push('Une adresse de facturation doit être renseignée');
    }
  }

  private manageNeedsTab(establishment: EstablishmentModel): void {
    const shouldShowNeedsTab = establishment?.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION;

    if (shouldShowNeedsTab && !this.needsTabAdded) {
      // Ajouter l'onglet s'il doit être visible et n'est pas déjà ajouté
      this.addTabs({
        label: 'FAMILLES ACHATS COUVERTES',
        route: './needs',
        permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT_NEEDS,
        index: 5,
      });
      // Maquette d'estimation « action d'équipe » : onglet ÉQUIPES (données fictives)
      this.addTabs({
        label: 'ÉQUIPES',
        route: './teams',
        permission: ENUM_PERMISSIONS.READ_ESTABLISHMENT,
        index: 9,
      });
      this.needsTabAdded = true;
    } else if (!shouldShowNeedsTab && this.needsTabAdded) {
      // Retirer l'onglet s'il ne doit plus être visible mais est présent
      const needsTab = this.tabs.find(tab => tab.label === 'FAMILLES ACHATS COUVERTES');
      if (needsTab) {
        this.removeTabs(needsTab);
        this.needsTabAdded = false;
      }
      const teamsTab = this.tabs.find(tab => tab.label === 'ÉQUIPES');
      if (teamsTab) {
        this.removeTabs(teamsTab);
      }
    }
  }
}
